import React from "react";
import { Divider, Checkbox, Typography, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import TagChip from "../tags/TagChip";
import {
  ExerciseTagDto,
  TagDto,
} from "@growth-machine-llc/stridist-api-client";
import { exerciseTagCategoryMultiSelectStyles } from "../exerciseLibraryStyles";
import FiltersOnlyThisButton from "../buttons/FiltersOnlyThisButton";
import { ALL_OPTION, NONE_OPTION } from "./filterUtils";

const useStyles = makeStyles((theme) => ({
  tag: {
    padding: theme.spacing(0.5, 1.5),
    height: 22,
    "& > *": {
      fontSize: 14,
    },
    alignItems: "center",
  },
  checkbox: {
    padding: 0,
    marginRight: theme.spacing(1),
  },

  onlyThisButton: {
    height: 22,
  },

  tagLocalOption: {
    "&:hover $onlyThisButton": {
      opacity: 1,
    },
  },
}));

export const NONE_TAG_ID = -1;

interface TagsFiltersContentProps {
  selectedTagIds: number[];
  onTagChange: (ids?: number[], allOptionsSelected?: boolean) => void;
  tags?: ExerciseTagDto[] | TagDto[];
}

export function TagsFiltersContent({
  selectedTagIds = [],
  onTagChange,
  tags = [],
}: TagsFiltersContentProps) {
  const s = useStyles();
  const classes = exerciseTagCategoryMultiSelectStyles();

  const allTagsWithNone = React.useMemo(
    () => [...tags.map((tag) => tag.id), NONE_TAG_ID],
    [tags],
  );

  const allIsSelected =
    selectedTagIds.length === allTagsWithNone.length &&
    allTagsWithNone.every((id) => selectedTagIds.includes(id));

  const noneIsSelected = selectedTagIds.includes(NONE_TAG_ID);

  React.useEffect(() => {
    if ((!selectedTagIds || selectedTagIds.length === 0) && tags.length > 0) {
      handleAllToggle();
    }
  }, [selectedTagIds, tags]);

  const isSelected = (id: number) => selectedTagIds.includes(id);

  const toggleTag = (tagId: number) => {
    const isAlreadySelected = selectedTagIds.includes(tagId);
    const newSelected = isAlreadySelected
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];

    onTagChange(newSelected.length === 0 ? [NONE_TAG_ID] : newSelected);
  };

  const handleAllToggle = () =>
    onTagChange(
      allIsSelected ? [NONE_TAG_ID] : allTagsWithNone,
      !allIsSelected,
    );

  const handleSelectNone = () =>
    onTagChange(
      noneIsSelected
        ? selectedTagIds.filter((id) => id !== NONE_TAG_ID)
        : [...selectedTagIds, NONE_TAG_ID],
    );

  const handleOnlyThisChange = (tagId: number) => {
    onTagChange(allTagsWithNone.filter((id) => id == tagId));
  };

  const TAG_FILTER_OPTIONS = [
    {
      id: ALL_OPTION,
      label: "All",
      checked: allIsSelected,
      onChange: handleAllToggle,
    },
    {
      id: NONE_OPTION,
      label: "None",
      checked: noneIsSelected,
      onChange: handleSelectNone,
    },
  ];

  return (
    <>
      {TAG_FILTER_OPTIONS.map((option) => (
        <Box
          className={clsx(classes.tagOption, s.tagLocalOption)}
          key={option.id}
          display="flex"
          alignItems="center"
          width="100%"
          onClick={option.onChange}
        >
          <Checkbox
            className={s.checkbox}
            color="primary"
            checked={option.checked}
            onChange={option.onChange}
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent={"space-between"}
            width="100%"
          >
            <Typography sx={{ fontWeight: 500 }}>{option.label}</Typography>
            {option.id === NONE_OPTION && (
              <FiltersOnlyThisButton
                handleOnlyThisChange={() => handleOnlyThisChange(NONE_TAG_ID)}
                className={s.onlyThisButton}
              />
            )}
          </Box>
        </Box>
      ))}

      <Divider />

      {tags.map((tag) => (
        <React.Fragment key={tag.id}>
          <Box
            className={clsx(classes.tagOption, s.tagLocalOption)}
            onClick={() => toggleTag(tag.id)}
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                className={s.checkbox}
                color="primary"
                checked={isSelected(tag.id)}
                onChange={() => toggleTag(tag.id)}
              />
            </Box>
            <Box display={"flex"} flexDirection="column" width="100%">
              <Box
                display="flex"
                alignItems="center"
                justifyContent={"space-between"}
              >
                <TagChip
                  primaryColor={tag.primaryColor}
                  className={s.tag}
                  text={tag.title}
                />
                <FiltersOnlyThisButton
                  handleOnlyThisChange={() => handleOnlyThisChange(tag.id)}
                  className={s.onlyThisButton}
                />
              </Box>
              <Typography className={classes.tagDescription}>
                {tag.description}
              </Typography>
            </Box>
          </Box>
          <Divider />
        </React.Fragment>
      ))}
    </>
  );
}
